import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { PipelineStage, PipelineStats } from '../types/crm';
import { logger } from '../utils/logger';

interface PipelineState {
  stages: PipelineStage[];
  isLoading: boolean;
  error: string | null;
  stats: PipelineStats | null;
  
  // Actions
  fetchStages: (empresaId: string) => Promise<void>;
  createDefaultStages: (empresaId: string) => Promise<void>;
  createStage: (stage: Omit<PipelineStage, 'id' | 'created_at' | 'updated_at'>, empresaId: string) => Promise<PipelineStage | null>;
  updateStage: (id: string, updates: Partial<PipelineStage>, empresaId: string) => Promise<PipelineStage | null>;
  deleteStage: (id: string, empresaId: string) => Promise<boolean>;
  reorderStages: (stageIds: string[], empresaId: string) => Promise<boolean>;
  fetchStats: (empresaId: string) => Promise<void>;
  clearError: () => void;
}

export const usePipelineStore = create<PipelineState>((set, get) => ({
  stages: [],
  isLoading: false,
  error: null,
  stats: null,

  fetchStages: async (empresaId: string) => {
    set({ isLoading: true, error: null });
    try {
      if (!empresaId || empresaId === 'sin-empresa') {
        logger.warn('No valid empresaId provided for fetching stages');
        set({ stages: [], isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('pipeline_stages')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('is_active', true)
        .order('stage_order', { ascending: true });

      if (error) {
        logger.error('Error fetching pipeline stages:', error);
        // If table doesn't exist or RLS blocking, provide helpful message
        if (error.code === 'PGRST116' || error.message?.includes('404') || error.message?.includes('relation')) {
          logger.warn('Pipeline stages table may not exist or RLS is blocking. Run the migration first.');
          set({ stages: [], isLoading: false, error: 'Pipeline stages table not found. Please run the migration.' });
          return;
        }
        throw error;
      }

      // CRITICAL: If no stages exist, auto-create them
      if (!data || data.length === 0) {
        logger.warn('⚠️ No pipeline stages found for empresa. Auto-creating default stages...');
        await get().createDefaultStages(empresaId);
        
        // Fetch again after creation
        const { data: newData, error: fetchError } = await supabase
          .from('pipeline_stages')
          .select('*')
          .eq('empresa_id', empresaId)
          .eq('is_active', true)
          .order('stage_order', { ascending: true });

        if (fetchError) {
          logger.error('Error fetching stages after auto-creation:', fetchError);
          set({ stages: [], isLoading: false, error: 'Failed to create default stages' });
          return;
        }

        logger.info('✅ Auto-created default pipeline stages:', newData?.length || 0);
        set({ stages: newData || [], isLoading: false });
        return;
      }

      logger.debug('✅ Fetched pipeline stages:', data?.length || 0);
      set({ stages: data || [], isLoading: false });
    } catch (error: any) {
      logger.error('Error fetching pipeline stages:', error);
      set({ error: error.message, isLoading: false, stages: [] });
    }
  },

  // Helper function to create default stages
  createDefaultStages: async (empresaId: string) => {
    const defaultStages = [
      { stage_name: 'Lead', stage_order: 1, stage_color: '#8B5CF6', is_won_stage: false, is_lost_stage: false },
      { stage_name: 'Contactado', stage_order: 2, stage_color: '#3B82F6', is_won_stage: false, is_lost_stage: false },
      { stage_name: 'Calificado', stage_order: 3, stage_color: '#06B6D4', is_won_stage: false, is_lost_stage: false },
      { stage_name: 'Propuesta Enviada', stage_order: 4, stage_color: '#F59E0B', is_won_stage: false, is_lost_stage: false },
      { stage_name: 'Negociación', stage_order: 5, stage_color: '#EF4444', is_won_stage: false, is_lost_stage: false },
      { stage_name: 'Ganado', stage_order: 6, stage_color: '#10B981', is_won_stage: true, is_lost_stage: false },
      { stage_name: 'Perdido', stage_order: 7, stage_color: '#6B7280', is_won_stage: false, is_lost_stage: true },
    ];

    try {
      // First try to call the database function (if it exists)
      const { error: functionError } = await supabase.rpc('create_default_pipeline_stages_for_empresa', {
        empresa_id_param: empresaId
      });

      if (functionError) {
        // Fallback: Create stages manually if function doesn't exist or fails
        logger.debug('Function not available or failed, creating stages manually...', functionError.message);
        const stagesToInsert = defaultStages.map(stage => ({
          ...stage,
          empresa_id: empresaId,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }));

        const { error: insertError } = await supabase
          .from('pipeline_stages')
          .insert(stagesToInsert);

        if (insertError) {
          logger.error('Error creating default stages manually:', insertError);
          throw insertError;
        }
        logger.info('✅ Created default stages manually');
      } else {
        logger.info('✅ Created default stages using database function');
      }
    } catch (error: any) {
      logger.error('Error in createDefaultStages:', error);
      throw error;
    }
  },

  createStage: async (stage, empresaId) => {
    try {
      const { data, error } = await supabase
        .from('pipeline_stages')
        .insert({
          ...stage,
          empresa_id: empresaId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        stages: [...state.stages, data],
      }));

      return data;
    } catch (error: any) {
      logger.error('Error creating pipeline stage:', error);
      set({ error: error.message });
      return null;
    }
  },

  updateStage: async (id, updates, empresaId) => {
    try {
      const { data, error } = await supabase
        .from('pipeline_stages')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('empresa_id', empresaId)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        stages: state.stages.map(s => s.id === id ? data : s),
      }));

      return data;
    } catch (error: any) {
      logger.error('Error updating pipeline stage:', error);
      set({ error: error.message });
      return null;
    }
  },

  deleteStage: async (id, empresaId) => {
    try {
      // Soft delete by setting is_active to false
      const { error } = await supabase
        .from('pipeline_stages')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('empresa_id', empresaId);

      if (error) throw error;

      set(state => ({
        stages: state.stages.filter(s => s.id !== id),
      }));

      return true;
    } catch (error: any) {
      logger.error('Error deleting pipeline stage:', error);
      set({ error: error.message });
      return false;
    }
  },

  reorderStages: async (stageIds, empresaId) => {
    try {
      // Update order for all stages
      const updates = stageIds.map((stageId, index) =>
        supabase
          .from('pipeline_stages')
          .update({ stage_order: index + 1, updated_at: new Date().toISOString() })
          .eq('id', stageId)
          .eq('empresa_id', empresaId)
      );

      await Promise.all(updates);

      // Refresh stages
      await get().fetchStages(empresaId);

      return true;
    } catch (error: any) {
      logger.error('Error reordering pipeline stages:', error);
      set({ error: error.message });
      return false;
    }
  },

  fetchStats: async (empresaId: string) => {
    try {
      // Fetch only clientes IN the pipeline (have pipeline_stage)
      const { data: clientes, error: clientesError } = await supabase
        .from('clientes')
        .select('pipeline_stage, valor_estimado, valor_cerrado, fecha_cierre, empresa_id')
        .eq('empresa_id', empresaId)
        .not('pipeline_stage', 'is', null);

      if (clientesError) {
        logger.error('Error fetching clientes for stats:', clientesError);
        throw clientesError;
      }

      // Calculate stats - handle null/undefined values
      // Only count clientes that are actually in the pipeline
      const clientesEnPipeline = clientes?.filter(c => c.pipeline_stage) || [];
      const totalClientes = clientesEnPipeline.length;
      const totalValorEstimado = clientesEnPipeline.reduce((sum, c) => sum + (Number(c.valor_estimado) || 0), 0);
      const totalValorCerrado = clientesEnPipeline.reduce((sum, c) => sum + (Number(c.valor_cerrado) || 0), 0);

      // Calculate closed this week/month
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const valorCerradoSemana = clientesEnPipeline.filter(
        c => c.fecha_cierre && new Date(c.fecha_cierre) >= weekAgo
      ).reduce((sum, c) => sum + (Number(c.valor_cerrado) || 0), 0);

      const valorCerradoMes = clientesEnPipeline.filter(
        c => c.fecha_cierre && new Date(c.fecha_cierre) >= monthAgo
      ).reduce((sum, c) => sum + (Number(c.valor_cerrado) || 0), 0);

      // Count by stage
      const clientesPorStage: Record<string, number> = {};
      clientesEnPipeline.forEach(c => {
        const stage = c.pipeline_stage || 'Sin Etapa';
        clientesPorStage[stage] = (clientesPorStage[stage] || 0) + 1;
      });

      set({
        stats: {
          totalClientes,
          totalValorEstimado,
          totalValorCerrado,
          valorCerradoSemana,
          valorCerradoMes,
          clientesPorStage,
        },
      });
    } catch (error: any) {
      logger.error('Error fetching pipeline stats:', error);
      set({ error: error.message });
    }
  },

  clearError: () => set({ error: null }),
}));

